import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import MapResults from "./MapResults";
import ResultSideBody from "./restaurantResults/ResultSideBody";
import Breadcrumb from "../common/Breadcrumb";
import ViewToggle from "../common/ViewToggle";
import OverviewPanel from "./discovery/OverviewPanel";
import { titleCaseLocationSlug } from "../../util/locationSlug";
import { stateAbbrev } from "../../util/stateAbbrev";
import { fetchDiscoveryData } from "../../util/discoveryApi";
import { buildLocationMetaDescription } from "../../util/seoDescriptionBuilder";

const overviewWrapperStyle = { flex: "1 1 auto" };

const CityMapPage = () => {
  const { state, city } = useParams();
  const [searchParams] = useSearchParams();
  const view = searchParams.get("view") === "overview" ? "overview" : "map";

  const cityDisplay = titleCaseLocationSlug(city);
  const stateDisplay = titleCaseLocationSlug(state);
  const abbr = stateAbbrev(stateDisplay);

  const [discoveryData, setDiscoveryData] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.__SSR_DATA__?.discovery || null;
  });
  const [discoveryLoaded, setDiscoveryLoaded] = useState(!!discoveryData);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.__SSR_DATA__) {
      delete window.__SSR_DATA__;
    }
  }, []);

  useEffect(() => {
    if (discoveryLoaded || fetchedRef.current) return;
    fetchedRef.current = true;
    fetchDiscoveryData(state, city)
      .then((data) => setDiscoveryData(data))
      .catch(() => setDiscoveryData(null))
      .finally(() => setDiscoveryLoaded(true));
  }, [state, city, discoveryLoaded]);

  const restaurants = useSelector((state) => state.restaurant.restaurants);

  const hasOverview = !!discoveryData?.cluster;
  const activeView = hasOverview ? view : "map";

  const heading = `${cityDisplay}, ${abbr}`;
  const overviewTitle = `Best Vegan Food in ${heading} | VegifyIt`;
  const mapTitle = `Vegan Food in ${heading} | VegifyIt`;
  const llmMetaDescription = discoveryData?.cluster?.seoMetaDescription || "";
  const fallbackDescription =
    restaurants && restaurants.length > 0
      ? buildLocationMetaDescription(restaurants, heading)
      : `Find vegan and vegan-friendly restaurants in ${heading}. Browse the map, filter by cuisine, and discover new spots.`;

  const title = activeView === "overview" ? overviewTitle : mapTitle;
  // LLM meta wins regardless of view — it's the curated SEO content. Falls
  // back to the dynamic templated description when no cluster prose exists.
  const description = llmMetaDescription || fallbackDescription;

  const overviewHidden = activeView !== "overview";
  const mapHidden = activeView !== "map";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <link
          rel="canonical"
          href={`https://www.vegifyit.com/cities/${state}/${city}`}
        />
      </Helmet>
      <Breadcrumb
        items={[
          { label: "Explore", href: "/explore" },
          { label: heading },
        ]}
        rightSlot={hasOverview ? <ViewToggle /> : null}
      />
      {hasOverview && (
        <div
          style={overviewHidden ? { display: "none" } : overviewWrapperStyle}
        >
          <OverviewPanel
            cluster={discoveryData.cluster}
            lists={discoveryData.lists}
            sampleRestaurants={discoveryData.sampleRestaurants}
            state={state}
            city={city}
          />
        </div>
      )}
      <div
        className={mapHidden ? "main-app-stash" : "main-app main-app--with-breadcrumb"}
        style={mapHidden ? { display: "none" } : undefined}
      >
        <MapResults isMarkerShown />
        <ResultSideBody />
      </div>
    </>
  );
};

export default CityMapPage;
